import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, X, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

/**
 * Contextual publish nudge — shows up at meaningful moments, never spammy.
 *
 * Rules:
 *   1. Only ever shows if user is unpaid AND has a generated profile
 *   2. Max once per browser session
 *   3. If dismissed (X button), suppressed for 24 hours via localStorage
 *   4. Conditional triggers — must hit at least one of:
 *      - Profile strength has just crossed 70%
 *      - Their profile is publish-ready (all required fields filled)
 *      - They've been editing/active on the page for 90+ seconds
 *
 * Style: small dismissable card slides up from bottom-right (desktop)
 * or bottom (mobile). Never blocks content. Premium aesthetic.
 */

const LS_DISMISSED_UNTIL = "esn_publish_nudge_dismissed_until";
const LS_SHOWN_SESSION = "esn_publish_nudge_shown_session";
const SESSION_ID_KEY = "esn_session_id";

const VARIANTS = {
  strong: {
    title: "Your profile is strong.",
    body: "Make it visible to scouts — one-off $19, fully refundable for 14 days.",
    cta: "Publish my profile",
  },
  ready: {
    title: "Looks ready to me.",
    body: "Every section's filled. Publish to the directory and start getting opened by scouts.",
    cta: "Publish — $19",
  },
  general: {
    title: "Want scouts to actually see this?",
    body: "Right now, only you can. $19 unlocks the directory + your shareable link. 14-day refund.",
    cta: "Make it visible",
  },
};

export default function PublishNudge() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState("general");
  const navigate = useNavigate();

  const sessionId = (() => {
    let s = sessionStorage.getItem(SESSION_ID_KEY);
    if (!s) { s = Math.random().toString(36).slice(2); sessionStorage.setItem(SESSION_ID_KEY, s); }
    return s;
  })();

  const canShow = useCallback(() => {
    if (!user || user.plan) return false; // paid users never see this
    const dismissedUntil = parseInt(localStorage.getItem(LS_DISMISSED_UNTIL) || "0", 10);
    if (Date.now() < dismissedUntil) return false;
    const shownInSession = localStorage.getItem(LS_SHOWN_SESSION);
    if (shownInSession === sessionId) return false;
    return true;
  }, [user, sessionId]);

  useEffect(() => {
    if (!user || user.plan) return;
    let cancelled = false;
    let strengthTimer, activityTimer;

    const trigger = (v) => {
      if (cancelled) return;
      if (!canShow()) return;
      setVariant(v);
      setVisible(true);
      localStorage.setItem(LS_SHOWN_SESSION, sessionId);
    };

    // Trigger A — check strength (delayed so the page can load)
    strengthTimer = setTimeout(async () => {
      try {
        const { data } = await api.get("/profile");
        if (cancelled) return;
        const score = data.strength?.score ?? 0;
        const publishReady = data.strength?.publish_ready;
        if (publishReady) trigger("ready");
        else if (score >= 70) trigger("strong");
      } catch {}
    }, 4000);

    // Trigger B — activity timer (90s on the page)
    activityTimer = setTimeout(() => trigger("general"), 90000);

    return () => {
      cancelled = true;
      clearTimeout(strengthTimer);
      clearTimeout(activityTimer);
    };
  }, [user, sessionId, canShow]);

  const handleDismiss = () => {
    const oneDay = 24 * 60 * 60 * 1000;
    localStorage.setItem(LS_DISMISSED_UNTIL, String(Date.now() + oneDay));
    setVisible(false);
  };

  const handleCTA = () => {
    setVisible(false);
    navigate("/paywall");
  };

  if (!visible) return null;
  const v = VARIANTS[variant];

  return (
    <div
      className="fixed z-[80] bottom-5 left-5 right-5 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[360px] animate-slide-up-soft"
      data-testid="publish-nudge"
    >
      <div className="relative bg-[#0E0E0E] border border-[#D4AF37]/40 rounded-md p-4 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7),0_0_0_1px_rgba(212,175,55,0.05)]">
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 text-[#666] hover:text-[#F5F5F5] transition-colors p-1"
          data-testid="publish-nudge-dismiss"
        >
          <X size={14} />
        </button>
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles size={11} className="text-[#D4AF37]" />
          <span className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-[0.22em]">A nudge from us</span>
        </div>
        <h4 className="text-sm font-bold text-[#F5F5F5] mb-1.5 leading-snug pr-5">{v.title}</h4>
        <p className="text-xs text-[#9a9a9a] leading-relaxed mb-3.5">{v.body}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCTA}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] px-3 py-2.5 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all"
            data-testid="publish-nudge-cta"
          >
            {v.cta} <ArrowRight size={11} />
          </button>
          <button
            onClick={handleDismiss}
            className="text-[10px] text-[#666] hover:text-[#A3A3A3] uppercase tracking-wider px-2 transition-colors"
            data-testid="publish-nudge-not-now"
          >
            Not now
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slide-up-soft {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up-soft { animation: slide-up-soft 350ms cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
}
